import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Box, Chip, Button } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import SearchInput from 'common/components/SearchInput';
import ProductsCategorySelect from 'components/ProductsCategorySelect';

import { PaymentStatus } from 'services/types';
import { setFilterStatus } from 'actions';
import { OrderInfo, ProductApi, Category } from 'common/types';
import { ConfirmedStatus, PreparedStatus } from 'services/types';
import { useTranslation } from 'services/localization/useTranslation';
import { getTranslation } from 'common/modules/translations';

import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import { getOrderPayments } from 'common/modules/atoms/paymentAmounts';
import RentalSearchBarFilterCheckbox from './RentalSearchFilterCheckbox';
import RentalSearchFilterDropdown from './RentalSearchFilterDropdown';

interface Props {
	rentals: OrderInfo[];
	onFilteredRentalsChange: (newFilteredRentals: null | OrderInfo[]) => void;
	searchInput: string;
	onSearchInputChange: (value: string) => void;
	mode: 'bookings' | 'active';
	onToggleExpired?: () => void;
	expiredRentalCount?: number;
	expiredRentalLimit?: number;
	onToggleDelivery?: () => void;
	deliveryRentalsCount?: number;
}

const RentalSearchFilter = (props: Props) => {
	const {
		rentals,
		onFilteredRentalsChange,
		onSearchInputChange,
		searchInput,
		onToggleExpired,
		expiredRentalCount,
		expiredRentalLimit,
		onToggleDelivery,
		deliveryRentalsCount,
	} = props;
	const { t } = useTranslation();
	const classes = useStyles();
	const dispatch = useDispatch();

	const filterStatus = useSelector(ViewSelectors.filterStatus);
	const lang = useSelector(LanguageSelectors.language);
	const bookingsViewExpiredChecked = useSelector(ViewSelectors.bookingsViewExpiredChecked);
	const bookingsViewDeliveryChecked = useSelector(ViewSelectors.bookingsViewDeliveryChecked);
	const [selectedProducts, setSelectedProducts] = useState<ProductApi[]>([]);
	const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);

	const filterApplied =
		filterStatus.payment !== PaymentStatus.All ||
		filterStatus.prepared !== PreparedStatus.All ||
		filterStatus.confirmed !== ConfirmedStatus.All ||
		!!selectedProducts.length ||
		!!selectedCategories.length;

	const paymentMenuItems = [
		{
			value: PaymentStatus.All,
			text: t('filter.status'),
		},
		{
			value: PaymentStatus.Paid,
			text: t('common:payment.paid'),
		},
		{
			value: PaymentStatus.Unpaid,
			text: t('common:payment.unpaid'),
		},
	];

	const statusMenuItems = [
		{
			value: ConfirmedStatus.All,
			text: t('filter.status'),
		},
		{
			value: ConfirmedStatus.Pending,
			text: t('common:states.onHold'),
		},
		{
			value: ConfirmedStatus.Confirmed,
			text: t('common:actions.confirmed'),
		},
	];

	const preparedMenuItems = [
		{
			value: PreparedStatus.All,
			text: t('filter.types'),
		},
		{
			value: PreparedStatus.NotPrepared,
			text: t('filter.notPrepared'),
		},
		{
			value: PreparedStatus.Prepared,
			text: t('filter.prepared'),
		},
	];

	useEffect(() => {
		const getFilteredRentals = () => {
			if (!filterApplied) {
				return null;
			}
			return rentals.filter((rental) => {
				const chargePayment = getOrderPayments(rental).charge;
				if (filterStatus.payment === PaymentStatus.Paid) {
					return chargePayment.unpaid <= 0;
				}
				if (filterStatus.payment === PaymentStatus.Unpaid) {
					return chargePayment.unpaid > 0;
				}
				if (filterStatus.prepared === PreparedStatus.NotPrepared && rental.prepared) {
					return false;
				}
				if (filterStatus.prepared === PreparedStatus.Prepared && !rental.prepared) {
					return false;
				}
				if (
					filterStatus.confirmed === ConfirmedStatus.Confirmed &&
					rental.activeState === 'PAYLINK_SENT'
				) {
					return false;
				}
				if (
					filterStatus.confirmed === ConfirmedStatus.Pending &&
					rental.activeState !== 'PAYLINK_SENT'
				) {
					return false;
				}
				if (
					selectedProducts.length &&
					selectedProducts.every((p) => !rental.stockProductIds.includes(p.id))
				) {
					return false;
				}
				if (
					selectedCategories.length &&
					selectedCategories.every((c) => !rental.categoryIds.includes(c.id))
				) {
					return false;
				}
				return true;
			});
		};
		onFilteredRentalsChange(getFilteredRentals());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		filterStatus.payment,
		filterStatus.prepared,
		filterStatus.confirmed,
		rentals,
		selectedProducts,
		selectedCategories,
		filterApplied,
	]);

	const handlePreparedStateChange = (event: any) => {
		const updatedFilterStatus = {
			...filterStatus,
			prepared: event.target.value,
		};
		dispatch(setFilterStatus(updatedFilterStatus));
	};

	const handleStatusChange = (event: any) => {
		const updatedFilterStatus = {
			...filterStatus,
			confirmed: event.target.value,
		};
		dispatch(setFilterStatus(updatedFilterStatus));
	};

	const handlePaymentStateChange = (event: any) => {
		const updatedFilterStatus = {
			...filterStatus,
			payment: event.target.value,
		};
		dispatch(setFilterStatus(updatedFilterStatus));
	};

	const handleProductsCategoryChange = ({
		products,
		categories,
	}: {
		products: ProductApi[];
		categories: Category[];
	}) => {
		setSelectedProducts(products);
		setSelectedCategories(categories);
	};

	const handleDeleteProductTag = (id: string) => {
		const updatedSelectedProducts = selectedProducts.filter((p) => p.id !== id);
		setSelectedProducts(updatedSelectedProducts);
	};

	const handleDeleteCategoryTag = (id: string) => {
		const updatedSelectedCategories = selectedCategories.filter((c) => c.id !== id);
		setSelectedCategories(updatedSelectedCategories);
	};

	const resetFilter = () => {
		const updatedFilterStatus = {
			...filterStatus,
			prepared: PreparedStatus.All,
			confirmed: ConfirmedStatus.All,
			payment: PaymentStatus.All,
		};
		setSelectedProducts([]);
		setSelectedCategories([]);
		dispatch(setFilterStatus(updatedFilterStatus));
	};

	const renderTags = () => {
		const categoryChips = selectedCategories.map((c) => (
			<Chip
				key={c.id}
				label={getTranslation(c.name, lang)}
				onDelete={() => handleDeleteCategoryTag(c.id)}
				className={classes.chip}
			/>
		));
		const productChips = selectedProducts.map((p) => (
			<Chip
				key={p.id}
				label={getTranslation(p.name, lang)}
				onDelete={() => handleDeleteProductTag(p.id)}
				className={classes.chip}
			/>
		));
		return [...categoryChips, ...productChips];
	};

	const renderFilter = () => (
		<>
			{renderDropdownFilters()}
			{renderCheckboxFilters()}
		</>
	);

	const renderDropdownFilters = () => (
		<Box className={classes.filterWrapperContainer}>
			<Box className={classes.filterWrapper}>
				<ProductsCategorySelect
					value={{ products: selectedProducts, categories: selectedCategories }}
					onChange={handleProductsCategoryChange}
				/>
			</Box>
			{props.mode === 'active' ? (
				<RentalSearchFilterDropdown
					type={'payment'}
					onChangeEvent={handlePaymentStateChange}
					menuItems={paymentMenuItems}
				/>
			) : (
				<>
					<RentalSearchFilterDropdown
						type={'status'}
						onChangeEvent={handleStatusChange}
						menuItems={statusMenuItems}
					/>
					<RentalSearchFilterDropdown
						type={'prepared'}
						onChangeEvent={handlePreparedStateChange}
						menuItems={preparedMenuItems}
					/>
				</>
			)}
		</Box>
	);

	const renderCheckboxFilters = () => (
		<Box className={classes.filterWrapperContainer}>
			{props.mode === 'active' ? (
				!!deliveryRentalsCount && (
					<RentalSearchBarFilterCheckbox
						showChecked={bookingsViewDeliveryChecked}
						onToggle={onToggleDelivery}
						textKey={t('filter.delivery')}
						badgeCount={deliveryRentalsCount}
					/>
				)
			) : (
				<>
					{!!deliveryRentalsCount && (
						<RentalSearchBarFilterCheckbox
							showChecked={bookingsViewDeliveryChecked}
							onToggle={onToggleDelivery}
							textKey={t('filter.delivery')}
							badgeCount={deliveryRentalsCount}
						/>
					)}
					<RentalSearchBarFilterCheckbox
						showChecked={bookingsViewExpiredChecked}
						onToggle={onToggleExpired}
						textKey={t('filter.expired')}
						badgeCount={expiredRentalCount}
						badgeCountLimit={expiredRentalLimit}
					/>
				</>
			)}
		</Box>
	);

	return (
		<Box my={2}>
			<Box className={classes.filterContainer}>
				<SearchInput
					searchValue={searchInput}
					className={classes.searchField}
					onChange={onSearchInputChange}
					textFieldProps={{
						placeholder: t('rentals.search.byNameOrProductCode') + '...',
						variant: 'outlined',
						size: 'small',
						InputProps: {
							classes: {
								notchedOutline: classes.inputNotchedOutline,
							},
							inputProps: {
								'data-qa': 'searchField',
							},
						},
					}}
					t={t}
				/>
				<Box className={classes.selectFilterContainer}>{renderFilter()}</Box>
			</Box>
			<Box display="flex">
				<Box flex="1">{renderTags()}</Box>
				<Box flex="0">
					{filterApplied && (
						<Button onClick={resetFilter}>{t('common:actions.clear', 'Clear')}</Button>
					)}
				</Box>
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		searchField: {
			flex: '1 0 auto',
			width: 'auto',
		},
		inputNotchedOutline: {
			border: 'none',
		},
		filterContainer: {
			marginBottom: theme.spacing(1),
			display: 'flex',
			flexWrap: 'wrap',
			border: `1px solid ${theme.palette.colors.tundra.main}`,
			borderRadius: theme.spacing(0.5),
			'@media (max-width:880px)': {
				flexDirection: 'column',
			},
			backgroundColor: theme.palette.background.paper,
		},
		selectFilterContainer: {
			flex: '0 1 auto',
			display: 'flex',
			flexWrap: 'wrap',
			borderLeft: `1px solid ${theme.palette.colors.tundra.main}`,
			'@media (max-width:880px)': {
				flex: '1 0 auto',
				borderLeft: 'none',
			},
		},
		filterWrapperContainer: {
			flex: '0 1 auto',
			display: 'flex',
			'@media (max-width:880px)': {
				flex: '1 0 auto',
				borderTop: `1px solid ${theme.palette.colors.tundra.main}`,
			},
			'&:first-child > div:first-child': {
				borderLeft: 'none',
			},
		},
		filterWrapper: {
			flex: '0 1 auto',
			flexWrap: 'wrap',
			paddingLeft: theme.spacing(2),
			borderLeft: `1px solid ${theme.palette.colors.tundra.main}`,
			'@media (max-width:880px)': {
				flex: '1 0 auto',
			},
			'@media (max-width:570)': {
				'&:first-child': {
					borderLeft: 'none',
				},
			},
			backgroundColor: theme.palette.background.paper,
		},
		chip: {
			margin: theme.spacing(1, 0.25),
		},
	}),
);

export default RentalSearchFilter;
